import { Component, ElementRef, HostBinding, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';

import { Properties } from './interfaces';
import { defaultProperties, backwardCompatibilityProperties } from './properties';
import { IvyPinch } from './ivypinch';

interface ComponentProperties extends Properties {
  disabled?: boolean;
  overflow?: 'hidden' | 'visible';
  disableZoomControl?: 'disable' | 'never' | 'auto';
  backgroundColor?: string;
}

export const DefaultComponentProperties: ComponentProperties = {
  overflow: 'hidden',
  disableZoomControl: 'auto',
  backgroundColor: 'rgba(0,0,0,0.85)',
};

type PropertyName = keyof ComponentProperties;

@Component({
  selector: 'app-pinch-zoom',
  templateUrl: './pinch-zoom.component.html',
  styleUrls: ['./pinch-zoom.component.scss'],
  standalone: true,
})
export class PinchZoomComponent implements OnDestroy, OnInit, OnChanges {
  #elementRef = inject(ElementRef);

  @Input() public disabled!: boolean;
  @Input() public disablePan!: boolean;
  @Input() public overflow!: 'hidden' | 'visible';
  @Input() public zoomControlScale!: number;
  @Input() public disableZoomControl!: 'disable' | 'never' | 'auto';
  @Input() public backgroundColor!: string;
  @Input() public limitPan!: boolean;
  @Input() public minPanScale!: number;
  @Input() public minScale!: number;
  @Input() public listeners!: 'auto' | 'mouse and touch';
  @Input() public wheel!: boolean;
  @Input() public autoHeight!: boolean;
  @Input() public wheelZoomFactor!: number;
  @Input() public draggableImage!: boolean;
  @Input() public properties: ComponentProperties;
  @Input() public transitionDuration!: number;
  @Input() public doubleTap!: boolean;
  @Input() public doubleTapScale!: number;
  @Input() public autoZoomOut!: boolean;
  @Input() public limitZoom!: number | 'original image size';

  #pinchZoom: IvyPinch;
  #defaultComponentProperties!: ComponentProperties;

  public constructor() {
    this.#defaultComponentProperties = this.getDefaultComponentProperties();
    this.applyPropertiesDefault(this.#defaultComponentProperties, {});
  }

  @HostBinding('style.overflow')
  public get hostOverflow() {
    return this.properties.overflow;
  }
  @HostBinding('style.background-color')
  public get hostBackgroundColor() {
    return this.properties.backgroundColor;
  }

  public get isTouchScreen() {
    const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
    const mq = (q: any) => window.matchMedia(q).matches;

    if ('ontouchstart' in window) {
      return true;
    }

    // include the 'heartz' as a way to have a non matching MQ to help terminate the join
    // https://git.io/vznFH
    const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
    return mq(query);
  }

  public get isDragging() {
    return this.#pinchZoom ? this.#pinchZoom.isDragging() : undefined;
  }

  public get isDisabled() {
    return this.properties.disabled;
  }

  public get scale() {
    return this.#pinchZoom.scale;
  }

  public get isZoomedIn() {
    return this.scale > 1;
  }

  public get scaleLevel() {
    return Math.round(this.scale / this.zoomControlScaleProperty);
  }

  public get maxScale() {
    return this.#pinchZoom.maxScale;
  }

  public get isZoomLimitReached() {
    return this.scale >= this.maxScale;
  }

  public get zoomControlScaleProperty() {
    return this.getPropertiesValue('zoomControlScale');
  }

  public ngOnInit() {
    this.initPinchZoom();

    /* Calls the method until the image size is available */
    this.detectLimitZoom();
  }

  public ngOnChanges(changes: SimpleChanges) {
    let changedProperties = this.getProperties(changes);
    changedProperties = this.renameProperties(changedProperties);

    this.applyPropertiesDefault(this.#defaultComponentProperties, changedProperties);
  }

  public ngOnDestroy() {
    this.destroy();
  }

  public initPinchZoom() {
    if (this.properties.disabled) {
      return;
    }

    this.properties.element = this.#elementRef.nativeElement.querySelector('.pinch-zoom-content');
    this.#pinchZoom = new IvyPinch(this.properties);
  }

  public getProperties(changes: SimpleChanges) {
    let properties: any = {};

    // eslint-disable-next-line guard-for-in
    for (const prop in changes) {
      if (prop !== 'properties') {
        properties[prop] = changes[prop].currentValue;
      }
      if (prop === 'properties') {
        properties = changes[prop].currentValue;
      }
    }
    return properties;
  }

  public renameProperties(properties: any) {
    for (const prop in properties) {
      if (backwardCompatibilityProperties[prop]) {
        properties[backwardCompatibilityProperties[prop]] = properties[prop];
        delete properties[prop];
      }
    }

    return properties;
  }

  public applyPropertiesDefault(initDefaultProperties: ComponentProperties, properties: ComponentProperties): void {
    this.properties = Object.assign({}, initDefaultProperties, properties);
  }

  public toggleZoom() {
    this.#pinchZoom.toggleZoom();
  }

  public isControl() {
    if (this.isDisabled) {
      return false;
    }

    if (this.properties.disableZoomControl === 'disable') {
      return false;
    }

    if (this.isTouchScreen && this.properties.disableZoomControl === 'auto') {
      return false;
    }

    return true;
  }

  public detectLimitZoom() {
    if (this.#pinchZoom) {
      this.#pinchZoom.detectLimitZoom();
    }
  }

  public destroy() {
    this.#pinchZoom.destroy();
  }

  public getPropertiesValue(propertyName: PropertyName) {
    if (this.properties && this.properties[propertyName]) {
      return this.properties[propertyName];
    } else {
      return this.#defaultComponentProperties[propertyName];
    }
  }

  public getDefaultComponentProperties() {
    return { ...defaultProperties, ...DefaultComponentProperties };
  }
}
